import { useEffect, useState } from 'react';
import {
  listApiKeys,
  generateApiKey,
  revokeApiKey,
  type AdminApiKey,
} from '../../lib/adminApi';
import { AdminPageShell, cls } from './shared';

export function ApiKeysPage() {
  const [keys, setKeys] = useState<AdminApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [shownKey, setShownKey] = useState<string | null>(null);

  function load() {
    listApiKeys()
      .then((r) => { setKeys(r.apiKeys); setLoading(false); })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleGenerate() {
    if (!newName.trim()) return;
    setGenerating(true);
    try {
      const result = await generateApiKey({ name: newName.trim() });
      setNewName('');
      setShownKey(result.rawKey);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke(key: AdminApiKey) {
    if (!confirm(`Revoke key "${key.name}"? Any integration using it will stop working immediately.`)) return;
    try {
      await revokeApiKey(key.id);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Revoke failed');
    }
  }

  if (loading) return <AdminPageShell title="API Keys"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="API Keys"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell title="API Keys">
      {/* One-time key reveal modal */}
      {shownKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-base font-semibold text-vari-navy mb-1">New API Key Generated</h2>
            <p className="text-sm text-vari-gray mb-4">
              Copy this key now — it will <strong>never be shown again</strong>.
            </p>
            <div className="bg-gray-50 border border-vari-soft-gray rounded-md p-3 font-mono text-sm break-all select-all mb-4 cursor-text">
              {shownKey}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { void navigator.clipboard.writeText(shownKey); }}
                className={cls.btnOutline}
              >
                Copy
              </button>
              <button
                onClick={() => setShownKey(null)}
                className={cls.btnSmPrimary}
              >
                Done — I've saved it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate form */}
      <div className={cls.formPanel}>
        <p className="text-sm font-medium text-vari-navy mb-3">Generate New API Key</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs text-vari-gray block mb-1">Key Name</label>
            <input
              className={cls.input}
              placeholder="e.g. Visualizer prod"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleGenerate(); }}
            />
          </div>
          <button
            onClick={() => void handleGenerate()}
            disabled={generating || !newName.trim()}
            className={cls.btnSmPrimary}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        <p className="text-xs text-vari-gray mt-2">
          The raw key is shown once on creation. Revocation is immediate and cannot be undone.
        </p>
      </div>

      {/* Keys table */}
      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>Name</th>
              <th className={cls.th}>Prefix</th>
              <th className={cls.th}>Created</th>
              <th className={cls.th}>Last used</th>
              <th className={cls.th}>Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k, i) => (
              <tr key={k.id} className={cls.tr(i % 2 === 1)}>
                <td className={cls.td}>{k.name}</td>
                <td className={cls.tdMono}>{k.keyPrefix}…</td>
                <td className={`${cls.td} text-vari-gray`}>
                  {new Date(k.createdAt).toLocaleDateString()}
                </td>
                <td className={`${cls.td} text-vari-gray`}>
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}
                </td>
                <td className={cls.td}>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      k.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {k.isActive ? 'active' : 'revoked'}
                  </span>
                </td>
                <td className={cls.tdRight}>
                  {k.isActive && (
                    <button
                      onClick={() => void handleRevoke(k)}
                      className={cls.btnDanger}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No API keys yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-vari-soft-gray text-xs text-vari-gray space-y-1.5">
        <p className="font-medium text-vari-navy text-sm">Integration usage</p>
        <p>The Visualizer should POST to <code className="font-mono bg-white px-1 py-0.5 rounded border border-vari-soft-gray">/api/integrations/renders</code> with:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li><code className="font-mono">Authorization: Bearer viis_…</code> header</li>
          <li>Multipart body: <code className="font-mono">layout_code</code>, <code className="font-mono">type</code>, <code className="font-mono">index</code> (1–5, required only when type=environmental), <code className="font-mono">file</code></li>
          <li><code className="font-mono">type</code> must be one of: <code className="font-mono">master</code>, <code className="font-mono">environmental</code>, <code className="font-mono">dimensional</code></li>
        </ul>
      </div>
    </AdminPageShell>
  );
}
